import {useEffect, useState} from 'react'

import { Box, styled } from '@mui/system'
import { AppBar, AppBarProps, Button, Grid, IconButton, List, ListSubheader, Paper, TextField, Toolbar, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import PersonalIcon from '@mui/icons-material/PersonOutlineTwoTone'
import ProfessionalIcon from '@mui/icons-material/EngineeringTwoTone';
import SocialIcon from '@mui/icons-material/GroupsTwoTone'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/CheckCircle'

import ConfirmationDialog from '../components/ConfirmationDialog'
import ErrorPaper from '../components/ErrorPaper'
import InfoPaper from '../components/InfoPaper'
import { useAuth } from '../components/AuthContext'

const PensieveBaseUri = 'http://api.epensieve.com';

const Styles = ({
    paper: {
        width: { xl: 1240, lg: 940, md: 640, sm: 480, xs: 320 },
        minWidth: { xl: 1200, lg: 900, md: 600, sm: 480, xs: 320 },
        margin: '15px auto',
        overflow: 'hidden',
    },
    sub_paper: {
        marginLeft: '5px',
        overflow: 'hidden',
    },
    topicBar: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
    topicText: {
        fontSize: 'primary',
    },
    addThought: {
        marginRight: 1,
    },
    block: {
        display: 'block',
    },
    contentWrapper: {
        margin: '10px',
    },
    thoughtHead: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    },
});

interface ThoughtHeaderProps extends AppBarProps {
    type: string;
}

const ThoughtHeader = styled(({children, ...props}: ThoughtHeaderProps) => (
    <AppBar {...props} position='static' color='default' elevation={0} >
        <Toolbar>{children}</Toolbar>
    </AppBar>
), {
    shouldForwardProp: (prop) => prop !== 'type',
})<ThoughtHeaderProps>(({type, theme}) => ({
    ...Styles.thoughtHead,
    ...((type === 'personal') && {
        backgroundColor: '#6AA1DB'
    }),
    ...((type === 'professional') && {
        backgroundColor: '#5BB85F'
    }),
    ...((type === 'social') && {
        backgroundColor: '#C28F4B'
    })
}))

type Link = {
    id: number,
    title: string,
    url: string
}

type Thought = {
    id: number,
    type: string,
    topic: string,
    text: string,
    links: Link[],
    modified: string,
}

function ThoughtNavigator() {
    const {token, userInfo} = useAuth()!;
    const default_topic = userInfo?.default_topic || 'Something';
    const [loading, setLoading] = useState(true);
    const [removalCandidate, setRemovalCandidate] = useState<number | null>(null);
    const [thoughts, setThoughts] = useState<Thought[]>([]);
    const [filter, setFilter] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    useEffect(() => {
        if (!token) return;

        fetch(`${PensieveBaseUri}/thoughts`, { method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` }
        }).then( (response) => {
            if (!response.ok) {
                throw new Error('Issue retrieving the detailed list of Topics');
            }
            return response.json();
        })
        .then((result) => {
            setThoughts(result.thoughts);
            setLoading(false);
        })
        .catch((err) => {
            setError(err.message);
        })
    }, [token]);

    const handleThoughtEdit = (id: number, field: string, value: any) => {
        setThoughts(thoughts.map((thought) => (thought.id !== id) ? thought : ({
            ...thought,
            [field]: value,
        })));
    };

    const handleAddThought = () => {
        fetch(`${PensieveBaseUri}/thoughts`, { method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
                topic: default_topic,
                text: '',
            })
        }).then( (response) => {
            if (!response.ok) {
                throw new Error('Could not Create Thought, try Refreshing or Logging In');
            }
            return response.text();
        }).then( (result) => {
            setInfo('Successfully Created Thought.');
            setTimeout(() => { setInfo(null); }, 1000);

            // Add Thought to the top of the list
            const links : Link[] = [];
            setThoughts(
                [{
                    id: +result,
                    type: 'personal',
                    topic: default_topic,
                    text:'',
                    links,
                    modified: (new Date()).toISOString()
                }].concat(thoughts)
            );
        })
        .catch((err) => {
            setError(err.message);
        })
    };

    const handleThoughtSave = (thought: Thought) => {
        fetch(`${PensieveBaseUri}/thoughts/${thought.id}`, { method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ 
                topic: thought.topic, 
                text: thought.text,
                links: thought.links,
                type: thought.type
            })
        }).then( (response) => {
            if (!response.ok) {
                throw new Error('Could not Update Thought, try Refreshing or Logging In');
            }

            setInfo('Successfully updated Thought.');
            setTimeout(() => { setInfo(null); }, 2000);

            // Move Thought to the top of the list
            setThoughts(
                [{...thought, modified: (new Date()).toISOString()}]
                .concat(thoughts.filter((t) => t.id !== thought.id))
            );
        })
        .catch((err) => {
            setError(err.message);
        })
    };

    const handleThoughtRemoval = () => {
        fetch(`${PensieveBaseUri}/thoughts/${removalCandidate!}`, { method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).then( (response) => {
            if (!response.ok) {
                throw new Error('Could not Delete Thought, try Refreshing or Logging In');
            }

            setInfo('Successfully removed Thought.');
            setTimeout(() => { setInfo(null); }, 2000);

            setThoughts( thoughts.filter((t) => t.id !== removalCandidate!) );
            setRemovalCandidate(null);
        })
        .catch((err) => {
            setError(err.message);
        })
    };

    if (loading) {
        return (<Paper sx={Styles.paper} > <Typography> Loading Thoughts ... </Typography> </Paper>);
    }

    return (
        <Box>
            { (error) && <ErrorPaper error={error} /> }
            { (info)  && <InfoPaper  msg={info} /> }
            <Paper sx={Styles.paper} >
                <AppBar sx={Styles.topicBar} position='static' color='default' elevation={0} >
                    <Toolbar>
                        <Grid container spacing={1} alignItems='center' >
                            <Grid item>
                                <SearchIcon color='inherit' sx={Styles.block} />
                            </Grid>
                            <Grid item xs >
                                <TextField
                                    fullWidth
                                    placeholder='Filter by Topic'
                                    variant='standard'
                                    onChange={(evt: any) => { setFilter(evt.currentTarget.value); }}
                                    sx={Styles.topicText}
                                    InputProps={{
                                        disableUnderline: true,
                                    }}
                                >
                                </TextField>
                            </Grid>
                            <Grid item>
                                <Button variant='contained' color='primary' sx={Styles.addThought}
                                    onClick={(evt: any) => {
                                        evt.preventDefault();
                                        handleAddThought();
                                    }} >
                                    Add Thought
                                </Button>
                            </Grid>
                        </Grid>
                    </Toolbar>
                </AppBar>
            </Paper>
            <Paper sx={Styles.paper} >
                <Grid container direction={'column'} sx={{...Styles.contentWrapper}} >
                    <Grid item sx={{margin: 0}} >
                        <List 
                            component='nav'
                            aria-labelledby='nested-list-subheader'
                            subheader={
                                <ListSubheader component='div' id='nested-list-subheader'>
                                    {(filter && `Filtering list by thoughts on topics starting with "${filter}"`)}
                                </ListSubheader>
                            }
                        ></List>
                    </Grid>
                    { thoughts
                        .filter((thought) => thought.topic.toUpperCase().startsWith(filter?.toUpperCase() || ''))
                        .map((thought) => (
                            <Grid item>
                                <Paper sx={{...Styles.sub_paper, width: '95%'}} >
                                    <ThoughtHeader sx={{height:'3.3vh'}} type={thought.type}>
                                        <Grid container spacing={1} alignItems='center'>
                                            <Grid item>
                                                {(thought.type === 'personal') ? <PersonalIcon /> : 
                                                 (thought.type === 'professional') ? <ProfessionalIcon /> : 
                                                 <SocialIcon /> }
                                            </Grid>
                                            <Grid item>
                                                <Typography>
                                                    {(thought.type === 'personal') ? 'Personal' : (thought.type === 'social') ? 'Social' : 'Professional' } Thought
                                                </Typography>
                                            </Grid>
                                            <Grid item xs />
                                            {(thought.type === 'personal') ? (<>
                                                <Grid item>
                                                    <IconButton onClick={() => {handleThoughtEdit(thought.id, 'type', 'professional')}} > 
                                                        <ProfessionalIcon />
                                                    </IconButton>
                                                </Grid>
                                                <Grid item>
                                                    <IconButton onClick={() => {handleThoughtEdit(thought.id, 'type', 'social')}} > 
                                                        <SocialIcon />
                                                    </IconButton>
                                                </Grid>
                                            </>) : (thought.type === 'professional') ? (<>
                                                <Grid item>
                                                    <IconButton onClick={() => {handleThoughtEdit(thought.id, 'type', 'social')}} > 
                                                        <SocialIcon />
                                                    </IconButton>
                                                    <IconButton onClick={() => {handleThoughtEdit(thought.id, 'type', 'personal')}} > 
                                                        <PersonalIcon />
                                                    </IconButton>
                                                </Grid>
                                            </>) : (<>
                                                <Grid item>
                                                    <IconButton onClick={() => {handleThoughtEdit(thought.id, 'type', 'personal')}} >
                                                        <PersonalIcon />
                                                    </IconButton>
                                                    <IconButton onClick={() => {handleThoughtEdit(thought.id, 'type', 'professional')}} > 
                                                        <ProfessionalIcon />
                                                    </IconButton>
                                                </Grid>
                                            </>)}
                                        </Grid>
                                    </ThoughtHeader>
                                    <Grid container direction='column' spacing={1} sx={{padding:2}}>
                                        <Grid item>
                                            <TextField 
                                                fullWidth size='small' label='Topic' 
                                                sx={{fontSize: 'large'}} 
                                                value={thought.topic}
                                                onChange={(evt) => { handleThoughtEdit(thought.id, 'topic', evt.target.value) }}
                                            />
                                        </Grid>
                                        <Grid item>
                                            <TextField 
                                                fullWidth multiline rows={5} 
                                                value={thought.text}
                                                onChange={(evt) => { handleThoughtEdit(thought.id, 'text', evt.target.value) }}
                                            />
                                        </Grid>
                                        <Grid item sx={{margin: '10px 5px 0 0'}}>
                                            <Grid container alignItems={'center'} spacing={2} >
                                                <Grid item>
                                                    <Typography fontSize={'small'} > Modified: {(new Date(thought.modified).toLocaleString())}</Typography>
                                                </Grid>
                                                <Grid item xs />
                                                <Grid item>
                                                    <IconButton onClick={() => { handleThoughtSave(thought) }}>
                                                        <SaveIcon />
                                                    </IconButton>
                                                </Grid>
                                                <Grid item>
                                                    <IconButton>
                                                        <DeleteIcon onClick={() => { setRemovalCandidate(thought.id); }} />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                        ))
                    }
                </Grid>
            </Paper>
            <ConfirmationDialog open={(removalCandidate != null)} title={'Remove this Thought?'} 
                handleClose={ () => { setRemovalCandidate(null); } }
                handleConfirm={ handleThoughtRemoval }
            >
                Are you sure you want to remove this Thought? This action is permanent and can't be undone.
            </ConfirmationDialog>
        </Box>
    )
}

export default ThoughtNavigator